import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Field, StringListField, TextArea } from './scenario-form/fields';
import { StagesField } from './scenario-form/StagesField';
import type { DraftExample, ScenarioDraft, DraftStyle } from './scenario-form/types';
import { ExamplesField } from './scenario-form/ExamplesField';

export interface ScenarioFormProps {
  draft: ScenarioDraft;
  onChange: (next: ScenarioDraft) => void;
}

/**
 * U6: a scenario as separate fields instead of one JSON blob. The persona
 * prompt, the facts and the rules are what the agent actually speaks from;
 * the stages are either a strict graph (`guided`) or just the goals of the
 * conversation (`soft`).
 */
export function ScenarioForm({ draft, onChange }: ScenarioFormProps) {
  const set = (part: Partial<ScenarioDraft>) => onChange({ ...draft, ...part });
  const style: DraftStyle = draft.style ?? {};
  const setStyle = (part: Partial<DraftStyle>) => set({ style: { ...style, ...part } });
  const soft = draft.stage_mode !== 'guided';

  return (
    <div className="space-y-4">
      <Field label="Цель сценария">
        <Input aria-label="Цель сценария" value={draft.goal ?? ''} onChange={(e) => set({ goal: e.target.value })} />
      </Field>

      <Field
        label="Промпт персоны: кто мы, что продаём, как говорим"
        hint="Свободный текст. Именно он задаёт живой разговор; этапы — только рамка."
      >
        <TextArea
          aria-label="Промпт персоны: кто мы, что продаём, как говорим"
          rows={5}
          placeholder="Мы — студия перформанс-маркетинга. Ведём VK Ads для малого бизнеса…"
          value={draft.persona_prompt ?? ''}
          onChange={(e) => set({ persona_prompt: e.target.value })}
        />
      </Field>

      <StringListField
        label="Факты о продукте"
        hint="Единственный источник истины: агенту запрещено утверждать что-либо сверх этого списка."
        placeholder="Первый аудит бесплатный"
        values={draft.facts ?? []}
        onChange={(facts) => set({ facts })}
      />

      <StringListField
        label="Правила"
        hint="Что можно и чего нельзя: «не обещать конкретный ROI», «не давить»."
        values={draft.rules ?? []}
        onChange={(rules) => set({ rules })}
      />

      <StringListField
        label="Стоп-темы"
        hint="При настойчивом вопросе по ним диалог уходит человеку."
        values={draft.stop_topics ?? []}
        onChange={(stop_topics) => set({ stop_topics })}
      />

      <Field label="Подсказка об отписке (opt-out)" hint="Дословно попадает в первое сообщение.">
        <Input
          aria-label="Подсказка об отписке (opt-out)"
          value={draft.opt_out_hint ?? ''}
          onChange={(e) => set({ opt_out_hint: e.target.value })}
        />
      </Field>

      <fieldset className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <legend className="mb-1 text-xs font-medium text-muted-fg">Стиль сообщений</legend>
        <Field label="Обращение">
          <Select
            aria-label="Обращение"
            value={style.address ?? 'вы'}
            onChange={(e) => setStyle({ address: e.target.value as DraftStyle['address'] })}
          >
            <option value="вы">на «вы»</option>
            <option value="ты">на «ты»</option>
          </Select>
        </Field>
        <Field label="Длина">
          <Select
            aria-label="Длина"
            value={style.message_length ?? 'short'}
            onChange={(e) => setStyle({ message_length: e.target.value as DraftStyle['message_length'] })}
          >
            <option value="short">короткие</option>
            <option value="medium">средние</option>
            <option value="long">развёрнутые</option>
          </Select>
        </Field>
        <Field label="Эмодзи">
          <Select
            aria-label="Эмодзи"
            value={style.emoji ?? 'none'}
            onChange={(e) => setStyle({ emoji: e.target.value as DraftStyle['emoji'] })}
          >
            <option value="none">без эмодзи</option>
            <option value="rare">редко</option>
            <option value="ok">можно</option>
          </Select>
        </Field>
        <Field label="Предел символов">
          <Input
            aria-label="Предел символов"
            type="number"
            min={80}
            max={1000}
            value={style.max_chars ?? ''}
            onChange={(e) => setStyle({ max_chars: e.target.value ? Number(e.target.value) : undefined })}
          />
        </Field>
      </fieldset>

      <Field
        label="Режим этапов"
        hint={
          soft
            ? 'Мягкий: этапы — цели разговора, агент сам решает, когда двигаться дальше.'
            : 'Жёсткий: агент идёт только по объявленным переходам.'
        }
      >
        <Select
          aria-label="Режим этапов"
          value={draft.stage_mode ?? 'soft'}
          onChange={(e) => set({ stage_mode: e.target.value as ScenarioDraft['stage_mode'] })}
        >
          <option value="soft">мягкий — живой разговор</option>
          <option value="guided">жёсткий — строгий граф</option>
        </Select>
      </Field>

      <Field label="Стартовый этап">
        <Input
          aria-label="Стартовый этап"
          value={draft.entry_stage ?? ''}
          onChange={(e) => set({ entry_stage: e.target.value })}
        />
      </Field>

      <StagesField stages={draft.stages ?? []} soft={soft} onChange={(stages) => set({ stages })} />

      <ExamplesField
        examples={draft.examples ?? []}
        onChange={(examples: DraftExample[]) => set({ examples })}
      />
    </div>
  );
}
